import { useMemo, useCallback, useEffect, useRef } from 'react'
import {
  Box,
  type EntitySelectorConfig,
  type EntitySelection,
  EntitySelector,
  EntitySelectorTrigger,
  EntitySelectorContent,
  EntitySelectorInput,
  EntitySelectorAutoTabs,
  EntitySelectorPool,
  EntitySelectorSelected,
  SelectTrigger,
} from '@salestrak/ui'
import { FilterToggle } from '#app/components/filters/filter-toggle'
import { useDashboardFilters } from '../lib/FilterContext'
import {
  getPresetShortLabel,
  type DatePresetValue,
} from '#app/lib/utils/date-presets'
import { DateRangePresetPicker } from '#app/components/filters/date-range-preset-picker'
import { teamToPoolItem } from '#app/components/entity-selectors/hooks/use-team-pool'
import { userToPoolItem } from '#app/components/entity-selectors/hooks/use-user-pool'

// --- Main FilterBar ---

export function FilterBar() {
  const {
    presetKey, customRange, setPreset,
    selectedTeams, setSelectedTeams,
    selectedReps, setSelectedReps,
    members,
    teams,
  } = useDashboardFilters()

  // Cascade: when teams selected, only show members belonging to those teams
  const visibleMembers = useMemo(() => {
    if (selectedTeams.length === 0) return members
    const selectedTeamSet = new Set(selectedTeams)
    return members.filter((member) =>
      (member.teams?.some((team) => selectedTeamSet.has(team.teamName)) ?? false)
    )
  }, [members, selectedTeams])

  // Auto-deselect members no longer visible after team filter change
  useEffect(() => {
    if (selectedTeams.length === 0) return
    const visibleMemberIds = new Set(visibleMembers.map((member) => member.userId))
    const nextSelectedReps = selectedReps.filter((repId) => visibleMemberIds.has(repId))
    if (nextSelectedReps.length !== selectedReps.length) {
      setSelectedReps(nextSelectedReps)
    }
  }, [selectedTeams, selectedReps, setSelectedReps, visibleMembers])

  // Combined entity selector config with both teams and members pools
  const entityConfig = useMemo<EntitySelectorConfig>(
    () => ({
      pools: [
        {
          id: 'teams',
          label: 'Teams',
          // The dashboard filter context keys teams by NAME, so the shared
          // item id is overridden; visuals/keywords stay shared.
          items: teams.map((t) => ({
            ...teamToPoolItem({ ...t, teamId: t.teamName }),
            description: `${t.memberCount + t.leadCount} members`,
          })),
        },
        {
          id: 'members',
          label: 'Members',
          items: visibleMembers.map((m) => {
            const item = userToPoolItem(m)
            return {
              ...item,
              description: m.teams?.[0]?.teamName ?? item.description,
              keywords: [
                ...(item.keywords ?? []),
                ...(m.teams?.map((t) => t.teamName) ?? []),
              ],
            }
          }),
        },
      ],
      searchPlaceholder: 'Search teams or members...',
      emptyMessage: 'No results found',
    }),
    [teams, visibleMembers],
  )

  // Build entity lookup keyed by pool:id to handle potential ID collisions across pools
  const entityByKey = useMemo(() => {
    const map = new Map<string, { poolId: string; entity: EntitySelection['entity'] }>()
    for (const pool of entityConfig.pools) {
      for (const item of pool.items) {
        map.set(`${pool.id}:${item.id}`, { poolId: pool.id, entity: item })
      }
    }
    return map
  }, [entityConfig])

  // Combine selectedTeams + selectedReps into EntitySelection[]
  const selections = useMemo<EntitySelection[]>(() => {
    const sel: EntitySelection[] = []
    for (const teamName of selectedTeams) {
      const entry = entityByKey.get(`teams:${teamName}`)
      if (entry) sel.push({ id: teamName, poolId: 'teams', entity: entry.entity })
    }
    for (const repId of selectedReps) {
      const entry = entityByKey.get(`members:${repId}`)
      if (entry) sel.push({ id: repId, poolId: 'members', entity: entry.entity })
    }
    return sel
  }, [selectedTeams, selectedReps, entityByKey])

  // Split selections back into teams and reps by poolId
  const handleSelectionChange = useCallback(
    (newSelections: EntitySelection[]) => {
      const newTeams: string[] = []
      const newReps: string[] = []
      for (const s of newSelections) {
        if (s.poolId === 'teams') newTeams.push(s.id)
        else if (s.poolId === 'members') newReps.push(s.id)
      }
      setSelectedTeams(newTeams)
      setSelectedReps(newReps)
    },
    [setSelectedTeams, setSelectedReps],
  )

  const totalFilterCount = selectedTeams.length + selectedReps.length
  const hasActiveFilters = totalFilterCount > 0

  // "1 Team & 2 Members" / "3 Teams" / "1 Member"
  const entityCountLabel = useMemo(() => {
    const parts: string[] = []
    if (selectedTeams.length > 0) {
      parts.push(`${selectedTeams.length} ${selectedTeams.length === 1 ? 'Team' : 'Teams'}`)
    }
    if (selectedReps.length > 0) {
      parts.push(`${selectedReps.length} ${selectedReps.length === 1 ? 'Member' : 'Members'}`)
    }
    return parts.join(' & ')
  }, [selectedTeams.length, selectedReps.length])

  // Hold the last non-empty label while the toggle collapses on clear,
  // so the user doesn't see an empty/zero label flash mid-animation.
  const lastNonEmptyLabelRef = useRef(entityCountLabel)
  if (entityCountLabel) lastNonEmptyLabelRef.current = entityCountLabel
  const displayedLabel = entityCountLabel || lastNonEmptyLabelRef.current

  const handleClearAll = useCallback(() => {
    setSelectedTeams([])
    setSelectedReps([])
  }, [setSelectedTeams, setSelectedReps])

  const dateValue = useMemo<DatePresetValue>(
    () => ({ key: presetKey, custom: customRange ?? undefined }),
    [presetKey, customRange],
  )
  const handleDateChange = useCallback(
    (next: DatePresetValue) => setPreset(next.key, next.custom),
    [setPreset],
  )

  return (
    <Box direction="row" align="center" gap="xs">
      <EntitySelector
        config={entityConfig}
        value={selections}
        onValueChange={handleSelectionChange}
      >
        <EntitySelectorTrigger
          render={
            <FilterToggle
            size="sm"
            icon="users"
            label={displayedLabel}
            selected={hasActiveFilters}
            onClear={handleClearAll}
            />
          }
        />
        <EntitySelectorContent size="sm" align="end">
          <EntitySelectorInput />
          <EntitySelectorAutoTabs />
          <EntitySelectorPool value="teams" />
          <EntitySelectorPool value="members" />
          <EntitySelectorSelected hideWhenEmpty />
        </EntitySelectorContent>
      </EntitySelector>
      <DateRangePresetPicker
        value={dateValue}
        onChange={handleDateChange}
      >
        <SelectTrigger chevron={false}
          render={
            <FilterToggle
            size="sm"
            icon="calendar"
            label={getPresetShortLabel(presetKey)}
            selected
            />
          }
        />
      </DateRangePresetPicker>
    </Box>
  )
}
